import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import { inr, num, dt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, QrCode, Trash2, Loader2, Activity, AlertTriangle, ShieldCheck, Layers, Store } from "lucide-react";
import { toast } from "sonner";

const isMerchantQr = (v) => v.merchant_qr === true || (v.merchant_qr == null && /(paytmqr|@ptys$|@ptm$)/i.test(v.vpa || ""));

export default function VPAs() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [qrVpa, setQrVpa] = useState(null);
  const [form, setForm] = useState({ vpa: "", payee_name: "", bank_label: "Paytm Business", daily_limit: "", mcc: "", merchant_qr: false });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vpas").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/vpas", {
        vpa: form.vpa.trim(),
        payee_name: form.payee_name.trim(),
        bank_label: form.bank_label,
        daily_limit: parseFloat(form.daily_limit) || 0,
        merchant_qr: form.merchant_qr,
        mcc: form.mcc.trim() || null,
      });
      toast.success("UPI ID added to rotation");
      setForm({ vpa: "", payee_name: "", bank_label: "Paytm Business", daily_limit: "", mcc: "", merchant_qr: false });
      setOpen(false);
      load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const bulkAdd = async () => {
    const items = bulkText.split("\n").map((line) => {
      const [vpa, payee_name, provider, limit] = line.split(",").map((s) => (s || "").trim());
      if (!vpa || !payee_name) return null;
      return { vpa, payee_name, provider: provider || "UPI", bank_label: provider || "UPI", daily_limit: parseFloat(limit) || 0 };
    }).filter(Boolean);
    if (items.length === 0) { toast.error("Add at least one line: upi@bank, Payee name"); return; }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/vpas/bulk", { items });
      toast.success(`${data.inserted} UPI ID(s) added to rotation`);
      setBulkText(""); setBulkOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };

  const toggle = async (v) => {
    await api.patch(`/vpas/${v.id}`, { active: !v.active });
    load();
  };

  const toggleVerified = async (v) => {
    try {
      await api.patch(`/vpas/${v.id}`, { verified: !v.verified });
      toast.success(v.verified ? "Marked unverified" : "Marked verified — preferred in rotation");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleMerchantQr = async (v) => {
    const next = !isMerchantQr(v);
    try {
      await api.patch(`/vpas/${v.id}`, { merchant_qr: next });
      toast.success(next ? "Merchant-QR link ON (Paytm P2M intent)" : "Merchant-QR link OFF (plain P2P)");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (v) => {
    await api.delete(`/vpas/${v.id}`);
    toast.success("UPI ID removed");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">UPI IDs & QR</h2>
          <p className="text-sm text-muted-foreground">Your collection pool — payments auto-rotate across active IDs.</p>
        </div>
        <div className="flex items-center gap-2">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="bulk-add-vpa-button"><Layers className="mr-1.5 h-4 w-4" /> Add multiple</Button>
          </DialogTrigger>
          <DialogContent data-testid="bulk-add-vpa-dialog">
            <DialogHeader>
              <DialogTitle>Add multiple UPI IDs</DialogTitle>
              <DialogDescription>One per line: <span className="font-mono">upi@bank, Payee name, Provider, DailyLimit</span>. Provider &amp; limit are optional.</DialogDescription>
            </DialogHeader>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={7}
              placeholder={"store1@okaxis, Sharma Electronics, Axis, 50000\nstore2@paytm, Sharma Electronics"}
              className="w-full rounded-md border bg-background p-3 font-mono text-sm"
              data-testid="bulk-vpa-textarea"
            />
            <DialogFooter>
              <Button onClick={bulkAdd} disabled={bulkSaving} data-testid="bulk-vpa-submit">
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add all to pool"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-vpa-button"><Plus className="mr-1.5 h-4 w-4" /> Add UPI ID</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-vpa-dialog">
            <DialogHeader>
              <DialogTitle>Add a UPI ID (VPA)</DialogTitle>
              <DialogDescription>Business VPAs on Paytm, PhonePe, BharatPe or a bank account.</DialogDescription>
            </DialogHeader>
            <form onSubmit={add} className="space-y-4">
              <div className="space-y-1.5">
                <Label>UPI ID / VPA</Label>
                <Input value={form.vpa} onChange={(e) => setForm({ ...form, vpa: e.target.value })}
                  placeholder="business@paytm" required data-testid="vpa-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Payee name</Label>
                <Input value={form.payee_name} onChange={(e) => setForm({ ...form, payee_name: e.target.value })}
                  placeholder="Your Business Name" required data-testid="vpa-payee-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Provider label</Label>
                  <Input value={form.bank_label} onChange={(e) => setForm({ ...form, bank_label: e.target.value })}
                    placeholder="Paytm Business" data-testid="vpa-label-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Daily limit (₹)</Label>
                  <Input type="number" min="0" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: e.target.value })}
                    placeholder="0 = no limit" data-testid="vpa-limit-input" />
                </div>
              </div>
              <div className="rounded-lg border bg-muted/40 p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Label className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-amber-600" /> Registered merchant VPA</Label>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Turn ON for a <b>Paytm for Business</b> / NPCI-registered merchant ID (e.g. …@ptys, paytmqr…).
                      Merchant VPAs avoid the “payment may be risky” prompt on PhonePe/Paytm. Leave OFF for a personal bank VPA.
                    </p>
                  </div>
                  <Switch checked={form.merchant_qr} onCheckedChange={(v) => setForm({ ...form, merchant_qr: v })} data-testid="vpa-merchant-switch" />
                </div>
                {form.merchant_qr && (
                  <div className="space-y-1.5">
                    <Label>Merchant category code (MCC)</Label>
                    <Input value={form.mcc} onChange={(e) => setForm({ ...form, mcc: e.target.value })}
                      placeholder="e.g. 4722" data-testid="vpa-mcc-input" />
                    <p className="text-[11px] text-muted-foreground">Use the exact MCC Paytm assigned and the exact registered business name above, or the app will reject the payment.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="vpa-submit-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to pool"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-xs text-muted-foreground" data-testid="vpa-guidance">
        <b className="text-amber-700 dark:text-amber-400">Personal vs Merchant VPAs:</b> payments to a <b>personal</b> bank UPI ID
        (e.g. …@pthdfc, …@oksbi) often trigger a “payment may be risky” safety prompt on PhonePe &amp; Paytm. To avoid this,
        add a <b>registered merchant VPA</b> (Paytm for Business) and turn on the <Store className="inline h-3 w-3 -mt-0.5" /> Merchant toggle — set its exact MCC &amp; business name.
      </div>

      {rows === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center">
          <QrCode className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">No UPI IDs yet</p>
          <p className="text-sm text-muted-foreground">Add your first VPA to start collecting.</p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((v) => (
            <Card key={v.id} className="p-5 flex flex-col gap-4" data-testid={`vpa-card-${v.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{v.bank_label}</Badge>
                    {v.verified && <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30" data-testid={`vpa-verified-${v.id}`}>Verified</Badge>}
                    {isMerchantQr(v) && <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30" data-testid={`vpa-merchantqr-${v.id}`}>Merchant QR</Badge>}
                    {v.active
                      ? <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">Active</Badge>
                      : v.auto_paused
                      ? <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">Auto-paused</Badge>
                      : <Badge variant="outline" className="text-[10px] text-muted-foreground">Paused</Badge>}
                  </div>
                  <p className="mt-2 font-mono text-sm font-medium truncate">{v.vpa}</p>
                  <p className="text-xs text-muted-foreground truncate">{v.payee_name}</p>
                </div>
                <Switch checked={v.active} onCheckedChange={() => toggle(v)} data-testid={`vpa-toggle-${v.id}`} />
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-muted/50 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Received</p>
                  <p className="text-sm font-semibold font-mono">{inr(v.total_received)}</p>
                </div>
                <div className="rounded-lg bg-muted/50 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Txns</p>
                  <p className="text-sm font-semibold font-mono">{num(v.txn_count)}</p>
                </div>
                <div className="rounded-lg bg-muted/50 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Limit</p>
                  <p className="text-sm font-semibold font-mono">{v.daily_limit ? `${num(v.daily_limit / 1000)}k` : "∞"}</p>
                </div>
              </div>

              {v.health && v.health !== "ok" && v.health !== "paused" && (
                <div
                  data-testid={`vpa-health-${v.id}`}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
                    v.health === "stalled" || v.health === "limit_reached"
                      ? "bg-destructive/10 text-destructive"
                      : "bg-warning/10 text-warning"
                  }`}
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {v.health_msg}
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="h-3 w-3" /> {v.last_used_at ? `Used ${dt(v.last_used_at)}` : "Never used"}
                </span>
                <div className="flex gap-1.5">
                  <Button size="icon" variant="outline" className={`h-8 w-8 ${isMerchantQr(v) ? "text-amber-600 border-amber-500/40" : ""}`} onClick={() => toggleMerchantQr(v)} data-testid={`vpa-merchantqr-toggle-${v.id}`} title={isMerchantQr(v) ? "Merchant-QR intent ON" : "Use Merchant-QR intent (Paytm)"}>
                    <Store className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" className={`h-8 w-8 ${v.verified ? "text-primary border-primary/40" : ""}`} onClick={() => toggleVerified(v)} data-testid={`vpa-verify-${v.id}`} title={v.verified ? "Verified (preferred)" : "Mark verified"}>
                    <ShieldCheck className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setQrVpa(v)} data-testid={`vpa-qr-${v.id}`}>
                    <QrCode className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => remove(v)} data-testid={`vpa-delete-${v.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!qrVpa} onOpenChange={(o) => !o && setQrVpa(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Static QR</DialogTitle>
            <DialogDescription>{qrVpa?.payee_name}</DialogDescription>
          </DialogHeader>
          {qrVpa && (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-xl bg-white p-4">
                <QRCodeSVG value={`upi://pay?pa=${qrVpa.vpa}&pn=${encodeURIComponent(qrVpa.payee_name)}&cu=INR`} size={200} />
              </div>
              <p className="font-mono text-sm">{qrVpa.vpa}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
